import { guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import {
  approveCompanyRegistration,
  rejectCompanyRegistration,
  getCompanyRegistrationByID,
} from '../../../api/company-registration.js';
import { CompanyRegistrationStatus } from '../../../db/enums/registration-status.js';
import { adminRouter } from './admin-routes.js';

export const AdminRegistrationDetail = stateful(({ registrationID }: { registrationID: string }) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [processing, setProcessing] = useState(false);

  const regQuery = useQuery(
    () =>
      getCompanyRegistrationByID(registrationID)({
        _id: 1,
        companyName: 1,
        contactFirstName: 1,
        contactLastName: 1,
        contactEmail: 1,
        address: 1,
        sector: 1,
        website: 1,
        status: 1,
        submittedAt: 1,
        reviewedAt: 1,
        resultingCompanyID: 1,
        rejectionReason: 1,
      }),
    [registrationID],
  );

  const reg = regQuery.value;

  const dateFormatter = new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Europe/Berlin',
  });

  const handleApprove = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      await guardDialog(() => approveCompanyRegistration(registrationID)());
      showDialog({ content: () => m.admin_registration_status_approved() });
    } catch (e: any) {
      console.error(e);
      showDialog({ content: () => e?.message ?? m.admin_registration_error() });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (processing) return;
    if (!rejectionReason.trim()) return;
    setProcessing(true);
    try {
      await guardDialog(() => rejectCompanyRegistration(registrationID, rejectionReason)());
      showDialog({ content: () => m.admin_registration_status_rejected() });
      setShowRejectForm(false);
    } catch (e: any) {
      console.error(e);
      showDialog({ content: () => e?.message ?? m.admin_registration_error() });
    } finally {
      setProcessing(false);
    }
  };

  if (regQuery.loading) {
    return html`<div class="flex justify-center py-8">
      <span class="loading loading-spinner loading-lg"></span>
    </div>`;
  }

  if (!reg) {
    return html`<div class="alert alert-error">${m.admin_registration_not_found()}</div>`;
  }

  const isPending = reg.status === CompanyRegistrationStatus.PENDING;
  const isApproved = reg.status === CompanyRegistrationStatus.APPROVED;
  const isRejected = reg.status === CompanyRegistrationStatus.REJECTED;

  const statusBadge = () => {
    if (isPending)
      return html`<span class="badge badge-warning badge-lg">${m.admin_registration_status_pending()}</span>`;
    if (isApproved)
      return html`<span class="badge badge-success badge-lg">${m.admin_registration_status_approved()}</span>`;
    if (isRejected)
      return html`<span class="badge badge-error badge-lg">${m.admin_registration_status_rejected()}</span>`;
    return nothing;
  };

  return html`
    <div>
      <div class="flex flex-col gap-4 mb-6">
        <button @click=${() => adminRouter.goTo('registrations')} class="btn btn-ghost self-start">
          <span class="icon-[material-symbols--arrow-back]"></span>
          ${m.back_to_list()}
        </button>
      </div>

      <div class="card bg-base-100 shadow-xl">
        <div class="card-body">
          <div class="flex justify-between items-start flex-wrap gap-2">
            <h2 class="card-title text-2xl">${reg.companyName}</h2>
            ${statusBadge()}
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <div class="text-sm font-semibold opacity-60">${m.admin_registration_contact_person()}</div>
              <div>${reg.contactFirstName} ${reg.contactLastName}</div>
              <div class="text-sm">${reg.contactEmail}</div>
            </div>
            ${
              reg.address
                ? html`<div>
                    <div class="text-sm font-semibold opacity-60">${m.company_registration_address_label()}</div>
                    <div>${reg.address}</div>
                  </div>`
                : nothing
            }
            ${
              reg.sector
                ? html`<div>
                    <div class="text-sm font-semibold opacity-60">${m.company_registration_sector_label()}</div>
                    <div>${reg.sector}</div>
                  </div>`
                : nothing
            }
            ${
              reg.website
                ? html`<div>
                    <div class="text-sm font-semibold opacity-60">${m.company_registration_website_label()}</div>
                    <a href="${reg.website}" target="_blank" class="link link-primary">${reg.website}</a>
                  </div>`
                : nothing
            }
            ${
              reg.submittedAt
                ? html`<div>
                    <div class="text-sm font-semibold opacity-60">${m.admin_registration_submitted_at()}</div>
                    <div>${dateFormatter.format(new Date(reg.submittedAt))}</div>
                  </div>`
                : nothing
            }
          </div>

          ${
            isRejected && reg.rejectionReason
              ? html`
                  <div class="alert alert-error mt-4">
                    <div>
                      <div class="font-semibold">${m.admin_registration_rejection_reason_label()}</div>
                      <div>${reg.rejectionReason}</div>
                    </div>
                  </div>
                `
              : nothing
          }
          ${
            isApproved && reg.resultingCompanyID
              ? html`
                  <div class="mt-4">
                    <button
                      class="btn btn-primary"
                      @click=${() =>
                        adminRouter.goTo('companyDetail', { params: { companyID: reg.resultingCompanyID! } })}
                    >
                      ${m.admin_registration_view_company()}
                    </button>
                  </div>
                `
              : nothing
          }
          ${
            isPending
              ? html`
                  <div class="divider mt-6"></div>
                  <div class="flex flex-wrap gap-3">
                    <button class="btn btn-success" ?disabled=${processing} @click=${handleApprove}>
                      ${processing ? html`<span class="loading loading-spinner loading-sm"></span>` : nothing}
                      ${m.admin_registration_approve_button()}
                    </button>
                    <button
                      class="btn btn-error btn-outline"
                      ?disabled=${processing}
                      @click=${() => setShowRejectForm(!showRejectForm)}
                    >
                      ${m.admin_registration_reject_button()}
                    </button>
                  </div>

                  ${
                    showRejectForm
                      ? html`
                          <div class="mt-4 space-y-3">
                            <label class="label">
                              <span class="label-text font-medium"
                                >${m.admin_registration_rejection_reason_label()}</span
                              >
                            </label>
                            <textarea
                              class="textarea textarea-bordered w-full"
                              rows="3"
                              placeholder=${m.admin_registration_rejection_reason_placeholder()}
                              .value=${rejectionReason}
                              @input=${(e: InputEvent) => setRejectionReason((e.target as HTMLTextAreaElement).value)}
                            ></textarea>
                            <button
                              class="btn btn-error"
                              ?disabled=${processing || !rejectionReason.trim()}
                              @click=${handleReject}
                            >
                              ${processing ? html`<span class="loading loading-spinner loading-sm"></span>` : nothing}
                              ${m.admin_registration_confirm_reject()}
                            </button>
                          </div>
                        `
                      : nothing
                  }
                `
              : nothing
          }
        </div>
      </div>
    </div>
  `;
});
